import { useEffect, useRef } from 'react';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';

interface HeatmapPoint {
  lat: number;
  lng: number;
  weight: number;
}

// US bounding box to filter out non-US data points
const US_BOUNDS = {
  minLat: 24.5,
  maxLat: 49.5,
  minLng: -125,
  maxLng: -66.5,
};

// Crime gradient: light green edges → yellow → orange → red hotspots (semi-transparent)
const COLOR_RANGE: [number, number, number, number][] = [
  [180, 230, 100, 120],  // pale green (very low)
  [220, 220, 50, 140],   // yellow-green
  [255, 200, 0, 160],    // yellow-orange
  [255, 120, 0, 170],    // orange
  [240, 60, 20, 180],    // red
  [220, 30, 30, 190],    // lighter red (highest) — not too dark so markers stay visible
];

export function useCrimeHeatmap(map: google.maps.Map | null, visible: boolean = false): void {
  const overlayRef = useRef<GoogleMapsOverlay | null>(null);
  const dataRef = useRef<HeatmapPoint[] | null>(null);
  const fetchingRef = useRef(false);

  // Main effect: reacts to map and visible changes
  useEffect(() => {
    if (!map) return;

    // If not visible, detach overlay and exit
    if (!visible) {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
      }
      return;
    }

    // Compute radius based on zoom level — larger radius when zoomed in
    // Radius doubles with each zoom level to maintain consistent coverage
    function getRadiusForZoom(zoom: number): number {
      const baseRadius = 80;
      const baseZoom = 4;
      // Each zoom level doubles the map scale, so radius must double per zoom to maintain coverage
      return Math.round(baseRadius * Math.pow(2, zoom - baseZoom));
    }

    function renderHeatmap(mapInstance: google.maps.Map, data: HeatmapPoint[]) {
      if (data.length === 0) return;

      // Compute log-scaled weights for smoother visualization
      const logWeights = data.map((d) => Math.log(d.weight + 1));
      const maxLog = Math.max(...logWeights);

      const normalizedData = data.map((d, i) => ({
        position: [d.lng, d.lat] as [number, number],
        weight: logWeights[i] / maxLog,
      }));

      const zoom = mapInstance.getZoom() || 5;
      const radius = getRadiusForZoom(zoom);

      // Scale intensity down gently as radius grows to prevent color saturation
      const baseIntensity = 2;
      const intensityScale = Math.pow(0.9, zoom - 4);
      const intensity = Math.max(baseIntensity * intensityScale, 0.8);

      const heatmapLayer = new HeatmapLayer({
        id: 'crime-heatmap',
        data: normalizedData,
        getPosition: (d: { position: [number, number]; weight: number }) => d.position,
        getWeight: (d: { position: [number, number]; weight: number }) => d.weight,
        radiusPixels: radius,
        intensity,
        threshold: 0.25,
        colorRange: COLOR_RANGE,
        aggregation: 'SUM' as const,
        weightsTextureSize: 512,
      });

      if (overlayRef.current) {
        overlayRef.current.setProps({ layers: [heatmapLayer] });
        overlayRef.current.setMap(mapInstance);
      } else {
        const overlay = new GoogleMapsOverlay({
          layers: [heatmapLayer],
        });
        overlay.setMap(mapInstance);
        overlayRef.current = overlay;
      }
    }

    // Re-render on zoom change so radius scales with zoom level
    const zoomListener = map.addListener('zoom_changed', () => {
      if (dataRef.current) {
        renderHeatmap(map, dataRef.current);
      }
    });

    // If data already cached, render immediately
    if (dataRef.current) {
      renderHeatmap(map, dataRef.current);
      return () => {
        google.maps.event.removeListener(zoomListener);
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }
      };
    }

    // Prevent duplicate fetches
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    fetch('/data/crimeHeatmap.json')
      .then((res) => res.json())
      .then((data: HeatmapPoint[]) => {
        const usData = data.filter(
          (d) =>
            d.lat >= US_BOUNDS.minLat &&
            d.lat <= US_BOUNDS.maxLat &&
            d.lng >= US_BOUNDS.minLng &&
            d.lng <= US_BOUNDS.maxLng
        );
        dataRef.current = usData;
        renderHeatmap(map, usData);
      })
      .finally(() => {
        fetchingRef.current = false;
      });

    return () => {
      google.maps.event.removeListener(zoomListener);
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
    };
  }, [map, visible]);
}
